import { VirtualClinicAnalysisOperatingNumberCalculation } from './calculation';
import {
  OverrideProperty,
  OverridePropertyHelper,
} from '@bindoc-projects/util-domain-lib';
import type { VirtualClinicAnalysisOperatingNumberTerm } from '../term/term';
import {
  BdChildResourceHelper,
  ShareableType,
} from '@bindoc-projects/resource-domain-lib';
import type {
  VirtualClinicAnalysisOperatingNumberSingleRequestPart,
  VirtualClinicAnalysisOperatingNumberTermSpec,
} from '../../resolving';

export class VirtualClinicAnalysisOperatingNumberSingleVirtualClinicCalculation extends VirtualClinicAnalysisOperatingNumberCalculation {
  public override readonly type: 'benchmarking' | 'single-virtual-clinic' =
    'single-virtual-clinic';

  constructor(
    term: VirtualClinicAnalysisOperatingNumberTerm,
    private _virtualClinicId: string,
  ) {
    super(term);
  }

  public updateVirtualClinicId(virtualClinicId: string): void {
    this._virtualClinicId = virtualClinicId;
    this.term.onUpdateVirtualClinicId(virtualClinicId);
  }

  override isValid(): boolean {
    return super.isValid() && !!this._virtualClinicId;
  }

  override toJSON(): {
    type: 'benchmarking' | 'single-virtual-clinic';
    term: VirtualClinicAnalysisOperatingNumberTermSpec | null;
    virtualClinicId: string;
  } {
    return {
      ...super.toJSON(),
      virtualClinicId: this._virtualClinicId,
    };
  }

  override getChildResources(): {
    [resourceName in ShareableType]?: string[];
  } {
    let childResources = super.getChildResources();

    if (this._virtualClinicId) {
      childResources = BdChildResourceHelper.merge(childResources, {
        [ShareableType.VIRTUAL_CLINIC]: [this._virtualClinicId],
      });
    }

    return childResources;
  }

  override getOverrideProperties(tileId: string): OverrideProperty[] {
    const result = super.getOverrideProperties(tileId, this._virtualClinicId);

    if (this._virtualClinicId) {
      result.push(
        new OverrideProperty(
          tileId,
          'virtualClinicId',
          this._virtualClinicId,
          this._virtualClinicId,
          'not-possible',
          undefined,
        ),
      );
    }

    return result;
  }

  public clone(): VirtualClinicAnalysisOperatingNumberSingleVirtualClinicCalculation {
    return new VirtualClinicAnalysisOperatingNumberSingleVirtualClinicCalculation(
      this._term.clone(),
      this._virtualClinicId,
    );
  }

  public overrideClone(
    override: OverrideProperty[],
  ): VirtualClinicAnalysisOperatingNumberCalculation {
    return new VirtualClinicAnalysisOperatingNumberSingleVirtualClinicCalculation(
      this._term.overrideClone(override),
      OverridePropertyHelper.overrideProperty(
        this._virtualClinicId,
        'virtualClinicId',
        override,
        (value) => value,
      ),
    );
  }

  public toRequestPart():
    | Omit<VirtualClinicAnalysisOperatingNumberSingleRequestPart, 'groupBy'>
    | null {
    if (this.isValid()) {
      return {
        type: 'VirtualClinicOperatingNumberSingle',
        term: this._term.toJSON(),
        virtualClinicId: this._virtualClinicId,
      };
    }

    return null;
  }

  get virtualClinicId(): string {
    return this._virtualClinicId;
  }

  set virtualClinicId(value: string) {
    this._virtualClinicId = value;
  }
}
